/* eslint-disable react/prop-types */
import { useEffect, useMemo } from 'react';

import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import MediaQuery from 'react-responsive';
import { matchPath, useHistory, useRouteMatch } from 'react-router';
import { compose, pure } from 'recompose';
import styled from 'styled-components';

import { getBankAccounts } from 'actions/account';
import { playWithdrawalVideo, playDepositVideo } from 'actions/settings';
import { showComponentOnSelectedRoute } from 'components/app/helpers';
import EmbedYoutubeModal from 'components/embedYoutubeModal';
import InrToggleButton from 'components/InrToggleButton/InrToggleButton';
import PrimaryHeader from 'components/primaryHeader';
import Render from 'components/render';
import { RenderForGlobalExchange } from 'components/render/RenderByExchangeType';
import {
  ADD_FUNDS_VIA_UPI_YOUTUBE_VIDEO,
  ADD_FUNDS_YOUTUBE_VIDEO,
  IS_INDIAN_EXCHANGE,
  SHOW_ACCOUNTS_MOBILE_HEADER,
} from 'constants/constants';
import { TRANSACTION_PARTNER_NAMES } from 'constants/enums';
import { ENABLE_INR_TOGGLE_IN_ACCOUNT_ANALYTICS } from 'constants/featureFlag';
import { MIXPANEL_EVENT_NAMES } from 'constants/mixpanelEventNames';
import routes from 'constants/routes';
import { trackMixpanelEvent } from 'helpers/mixpanel-init';
import { device } from 'helpers/screenSizes';
import { noop } from 'helpers/utils';
import useHistoryGoBack from 'hooks/useBackHistory';
import useLanguageChange from 'hooks/useLanguageChange';
import useToggle from 'hooks/useToggle';
import i18n from 'i18n/config';
import { selectActiveDepositPartner } from 'selectors/userBankDetails';
import { useAppSelector } from 'storeHooks';
import {
  DoubleArrowLeft,
  HistoryClock,
  PlayIcon,
  AngleLeft,
  NewYoutubeTutorialIcon,
} from 'styles/assets/icons';
import { ADD_BANK_CUSTODIAN_STATUS } from 'types/Account';
import Box from 'UIKit/Box';
import IconButton from 'UIKit/IconButton';

import DepositAccountHeaderCenter from './accountHeaderPartials/DepositAccountHeader';
import MfaSettingsHeader from './accountHeaderPartials/MfaSettingsHeader';
import { SignalTradingAccountHeader } from './accountHeaderPartials/SignalTradingHeader';
import { WithdrawalBankIndiaHeader } from './accountHeaderPartials/WithdrawalBankIndiaHeader';
import WithdrawalMobileHeader from './accountHeaderPartials/WithdrawalPageHeader';
import styles from './header.module.scss';
import HeaderStyle from './header_style';
import { LogoComponent } from './partials';

const AccountHeaderWrapper = styled(HeaderStyle)`
  top: 0;
  z-index: 30;
  @media ${device.down.md} {
    z-index: 30;
  }
`;

const HeadWrapper = styled.div`
  width: 100%;
  .menu-wrapper {
    position: fixed;
    right: 0;
    top: 0;
  }
  .back-link {
    margin-left: 70px;
    font-family: var(--fontRegular);
    cursor: pointer;
    svg {
      margin: 0px 2px 2px 0px;
    }
    @media ${device.down.md} {
      margin-left: 0px;
    }
  }
`;

const SecondHeaderWrapper = styled.div`
  height: 48px;
  width: 100%;
  background-color: var(--main-bg-surface-alt);
  font-size: 16px;
  font-family: var(--fontRegular);
  letter-spacing: 0.6px;
  text-align: center;
  color: var(--main-text-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 0 !important;
  border-bottom: 1px solid var(--seperator-subtle);

  .backButton {
    height: 100%;
    border: none;
    position: absolute;
    left: 5px;
    width: 40px;
    vertical-align: baseline;
    z-index: 1;

    &.deposit-link {
      display: flex;
      align-items: center;
      width: auto;
      font-size: 14px;
      color: var(--main-text-secondary);
    }
  }
  .deposit-tabs {
    font-size: 14px;
    line-height: 18px;
    color: var(--main-text-secondary);
    position: absolute;
    left: 50px;
    display: grid;
    grid-template-columns: max-content max-content;
    gap: 25px;
    height: 100%;
    align-items: flex-end;

    @media ${device.down.xxs} {
      gap: 12px;
    }

    .tab {
      position: relative;
      height: 30px;
      margin-bottom: 5px;
      border-bottom: solid 3px transparent;
      span {
        display: inline-block;
        padding-bottom: 8px;
      }
      &.active {
        font-weight: 600;
        color: var(--main-text-primary);
        border-bottom: solid 3px var(--brand-border-primary);
      }
      &.buyCrypto {
        svg {
          margin-left: 5px;
          margin-bottom: 3px;
        }
      }
    }
  }
  .deposit-tabs-scrollable {
    @media (max-width: 414px) {
      max-width: 202px;
      overflow-x: scroll;
      scroll-behavior: smooth;
    }
  }
  .history-icon {
    position: absolute;
    right: 20px;
    svg {
      width: 16px;
      height: 16px;
    }
  }
  .addFundsIcon {
    position: absolute;
    right: 50px;
    margin-right: 10px;
  }

  .icons-container {
    position: absolute;
    right: 45px;
    top: 10px;
    display: flex;
    // gap: 15px;
    a.pnlAnalyticsButton {
      min-width: 20px;
      background: var(--main-bg-primary);
      border-radius: 4px;
      border: none;
      width: 28px;
      height: 28px;
      padding: 0;
      display: flex;
      justify-content: center;
      align-items: center;
    }
    button {
      width: 28px;
      height: 28px;
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
  .play-video {
    position: absolute;
    right: 60px;
    svg {
      path {
        fill: var(--main-text-secondary);
      }
      width: 16px;
      height: 16px;
      margin-top: -1px;
    }
  }
  .inrUsdToggleButton {
    position: absolute;
    right: 20px;
  }
`;

const LogoMenuHeader = () => {
  // const {
  //   burgerMenuIsOpen,
  //   theme,
  //   isloggedin,
  //   toggleMenu,
  //   logout,
  //   toggleTheme,
  //   selectedProduct,
  // } = props;
  const { t } = useTranslation(['account']);
  const history = useHistory();
  const pathdata = window.location.pathname;
  const isIdentityVerification = pathdata.includes('level');

  const onBackToTradingClick = () => {
    trackMixpanelEvent('Back to Trading clicked - All Pages - Header');
    history.push(
      isIdentityVerification
        ? `${routes.account.identityVerification}`
        : `${routes.default}`
    );
  };

  return (
    <HeadWrapper className="d-flex header-wrapper">
      <LogoComponent />
      <div
        role="button"
        tabIndex="0"
        onKeyDown={noop}
        data-testid="yN5Zu-qfg-jVEinAlxNKy"
        className="back-link order-2 align-self-center"
        onClick={onBackToTradingClick}
      >
        <DoubleArrowLeft />{' '}
        {isIdentityVerification ? t('account:backButton') : t('account:backTrading')}
      </div>
      {/* <div style={{ position: 'absolute', right: '20px', top: '15px' }}>
        <ToggleThemeSwitch />
  </div> */}
      {/* <div className="menu-wrapper">
        <MediaQuery query={device.down.md}>
          <Menu
            burgerMenuIsOpen={burgerMenuIsOpen}
            theme={theme}
            isloggedin={isloggedin}
            toggleMenu={toggleMenu}
            logout={logout}
            toggleTheme={toggleTheme}
            selectedProduct={selectedProduct}
          />
        </MediaQuery>
      </div> */}
    </HeadWrapper>
  );
};

const VIDEO_PARAMS_FOR_DEPSOIT_METHODS = {
  [TRANSACTION_PARTNER_NAMES.VAN]: {
    testId: 'add-funds-play-video-btn-mobile',
    videoUrl: ADD_FUNDS_YOUTUBE_VIDEO,
    type: 'Bank Transfer',
  },
  [TRANSACTION_PARTNER_NAMES.DE_INTERNAL]: {
    testId: 'add-funds-via-upi-play-video-btn-mobile',
    videoUrl: ADD_FUNDS_VIA_UPI_YOUTUBE_VIDEO,
    type: 'UPI',
  },
};

const VideoTutorialForDepositIndia = () => {
  const [videoPlay, toggleVideoPlay] = useToggle(false);
  const depositPartner = useAppSelector(selectActiveDepositPartner);

  const { type, testId, videoUrl } = VIDEO_PARAMS_FOR_DEPSOIT_METHODS[depositPartner];

  const handleAddFundsClick = () => {
    toggleVideoPlay();
    trackMixpanelEvent(MIXPANEL_EVENT_NAMES.ADD_FUNDS_VIDEO_TUTORIAL, {
      type,
    });
  };

  return (
    <>
      <IconButton
        onKeyDown={noop}
        data-testid={testId}
        onClick={handleAddFundsClick}
        className="addFundsIcon"
        icon={<NewYoutubeTutorialIcon />}
      />
      <EmbedYoutubeModal src={videoUrl} onClose={toggleVideoPlay} isOpen={videoPlay} />
    </>
  );
};

const addFundsText = i18n.t('headerFooter:header.wallet.addFunds');
const allTransactionsText = i18n.t('account:allTransactions');

export const ArrowHeader = ({ showTitle = true }) => {
  const { t } = useTranslation(['account']);
  const history = useHistory();
  const dispatch = useDispatch();
  const match = useRouteMatch(routes.account.accountAnalytics.default);
  const { pathname, state: routeState } = history.location;
  const isAccountAnalyticsPage = match ? match.isExact : false;
  const [isBankAccountAdded, toggleIsBankAccountAdded] = useToggle(false);
  const { goBack } = useHistoryGoBack();

  useEffect(() => {
    if (
      matchPath(pathname, {
        path: routes.account.deposit,
      })
    ) {
      dispatch(getBankAccounts()).then(response => {
        const isBankAdded = response?.result?.bank_details?.some(
          bank =>
            bank.custodian_status === ADD_BANK_CUSTODIAN_STATUS.INITIATED ||
            bank.custodian_status === ADD_BANK_CUSTODIAN_STATUS.IN_PROGRESS ||
            bank.custodian_status === ADD_BANK_CUSTODIAN_STATUS.PENDING ||
            bank.custodian_status === ADD_BANK_CUSTODIAN_STATUS.COMPLETED
        );
        toggleIsBankAccountAdded(isBankAdded);
      });
    }
  }, [dispatch, pathname, toggleIsBankAccountAdded]);

  const backButtonClick = () => {
    if (
      matchPath(pathname, {
        path: routes.account.identityVerification,
      })
    ) {
      history.push(routes.homepage.default);
      return;
    }

    if (
      matchPath(pathname, {
        path: routes.account.deposit,
      }) &&
      routeState?.redirectedFromAddAccount &&
      isBankAccountAdded
    ) {
      history.replace(routes.homepage.default);
      return;
    }

    goBack();
  };

  const onHistoryClick = () => {
    // '/' is important as some other routes can have 'withdrawal' in it .
    if (
      pathname.includes(`${routes.account.withdrawal}/`) ||
      pathname.endsWith('withdrawal')
    ) {
      history.push(routes.account.withdrawal_history);
    } else if (
      pathname.includes(`${routes.account.withdrawalBank}/`) ||
      pathname.endsWith('withdrawal_bank')
    ) {
      if (IS_INDIAN_EXCHANGE) {
        history.push(routes.account.withdrawal_history);
      } else {
        history.push(routes.account.withdrawal_bank_history);
      }
    } else if (pathname.includes('deposit')) {
      history.push(routes.account.deposit_history);
    } else if (pathname.includes('balances')) {
      history.push(routes.account.transactionLog.asset_history);
    } else if (pathname.includes('buy_crypto')) {
      history.push(routes.account.fiat_deposit_history);
    }
  };

  const playVideo = () => {
    if (pathname.includes('/deposit/') || pathname.endsWith('/deposit')) {
      dispatch(playDepositVideo(true));
      trackMixpanelEvent('Video Tutorial Opened - Deposit');
    } else if (pathname.includes('/withdrawal/') || pathname.endsWith('/withdrawal')) {
      trackMixpanelEvent('Video Tutorial Opened - Withdrawal');
      dispatch(playWithdrawalVideo(true));
    }
  };

  const isDepositPage = Boolean(
    matchPath(pathname, {
      path: routes.account.deposit,
    })
  );

  const CenterElement = useMemo(() => {
    if (!IS_INDIAN_EXCHANGE) {
      if (pathname.includes('/buy_crypto') || pathname.includes('/deposit')) {
        return <DepositAccountHeaderCenter pathname={pathname} />;
      }

      if (
        pathname.includes(`${routes.account.withdrawal}/`) ||
        pathname.endsWith('/withdrawal') ||
        pathname.includes(`${routes.account.withdrawalBank}/`) ||
        pathname.endsWith('withdrawal_bank')
      ) {
        return <WithdrawalMobileHeader pathname={pathname} />;
      }
    }

    if (pathname.endsWith('/signal_trading') || pathname.endsWith('/signal_trading/')) {
      return <SignalTradingAccountHeader />;
    }

    if (pathname.endsWith('/withdrawal_bank') && IS_INDIAN_EXCHANGE) {
      return <WithdrawalBankIndiaHeader />;
    }

    if (pathname.endsWith('settings_2fa')) {
      return <MfaSettingsHeader />;
    }

    if (isDepositPage && IS_INDIAN_EXCHANGE) {
      return <Box fontSize="sm4">{addFundsText}</Box>;
    }

    const isDepositHistoryPage = Boolean(
      matchPath(pathname, {
        path: routes.account.deposit_history,
      })
    );

    if (isDepositHistoryPage && IS_INDIAN_EXCHANGE) {
      return <Box fontSize="sm4">{allTransactionsText}</Box>;
    }

    return <Box fontSize="sm4">{t(`account:pathNames.${pathname}`)}</Box>;
  }, [isDepositPage, pathname, t]);

  return (
    <SecondHeaderWrapper>
      <button
        type="button"
        data-testid="_K8ikN6RGyMhaACM2mTKt"
        onClick={() => backButtonClick()}
        className="backButton"
      >
        {/* <FaChevronLeft />
         */}
        <AngleLeft className={styles.arrowLeftIcon} />
      </button>
      {showTitle ? CenterElement : null}
      {/* <span className="header-text">{t(`account:pathNames.${pathname}`)}</span> */}

      <RenderForGlobalExchange>
        {(pathname.includes('/withdrawal/') ||
          pathname.endsWith('/withdrawal') ||
          pathname.includes('/deposit/') ||
          pathname.endsWith('/deposit')) && (
          <IconButton
            onKeyDown={noop}
            data-testid="DepositsPage-play-video-btn-mobile"
            className={cn('play-video', styles.headerActionBtn)}
            onClick={playVideo}
            icon={<PlayIcon className="play-icon" />}
          />
        )}
      </RenderForGlobalExchange>
      {(pathname.includes('/withdrawal/') ||
        pathname.endsWith('withdrawal') ||
        pathname.includes('/withdrawal_bank') ||
        isDepositPage ||
        pathname.includes('balances') ||
        pathname.includes('buy_crypto')) &&
        !pathname.includes('withdrawal_history') && (
          <IconButton
            onKeyDown={noop}
            data-testid="DepositsPage-history-btn-mobile"
            className={cn('history-icon', styles.headerActionBtn)}
            onClick={onHistoryClick}
            icon={<HistoryClock />}
          />
        )}
      <Render when={IS_INDIAN_EXCHANGE && isDepositPage}>
        <VideoTutorialForDepositIndia />
      </Render>
      {ENABLE_INR_TOGGLE_IN_ACCOUNT_ANALYTICS && isAccountAnalyticsPage && (
        <MediaQuery query={device.down.sm}>
          <div className="inrUsdToggleButton">
            <InrToggleButton />
          </div>
        </MediaQuery>
      )}
    </SecondHeaderWrapper>
  );
};

// used when back button doesn't send back to history and we want custom header text
export const ArrowOverlayHeader = ({ onBackButtonClick, headerText }) => {
  return (
    <SecondHeaderWrapper>
      <button
        type="button"
        data-testid="s5rqIBdyLbDxOk8RYUkvY"
        onClick={() => onBackButtonClick()}
      >
        {/* <BackArrow /> */}
        {/* <FaChevronLeft />
         */}
        <AngleLeft />
      </button>
      <span>{headerText}</span>
    </SecondHeaderWrapper>
  );
};

const AccountHeader = props => {
  const {
    isloggedin,
    theme,
    burgerMenuIsOpen,
    toggleMenu,
    logout,
    toggleTheme,
    selectedProduct,
  } = props;
  /**
   * Since main navbar with localization change is removed in this page, we call "useLanguageChange(false)" to
   * allow i18 library to detect language. "false" is passed is prevent unnecessary calling of i18.changeLanguage func
   */
  useLanguageChange(false);
  const location = useSelector(state => state.router.location);

  return (
    (<AccountHeaderWrapper isloggedin={isloggedin}>
      <MediaQuery query={device.up.md}>
        <PrimaryHeader isloggedin={isloggedin} />
      </MediaQuery>
      <MediaQuery query={device.down.md}>
        {showComponentOnSelectedRoute(SHOW_ACCOUNTS_MOBILE_HEADER)(location) ? (
          // eslint-disable-next-line react/jsx-props-no-spreading
          (<ArrowHeader {...props} />)
        ) : (
          <LogoMenuHeader
            burgerMenuIsOpen={burgerMenuIsOpen}
            theme={theme}
            isloggedin={isloggedin}
            toggleMenu={toggleMenu}
            logout={logout}
            toggleTheme={toggleTheme}
            selectedProduct={selectedProduct}
          />
        )}
      </MediaQuery>
    </AccountHeaderWrapper>)
  );
};

const enhance = compose(pure);

export default enhance(AccountHeader);
